// Ported (subset) from dev-scrips-pm-react src/scheduling/lib/device-edit-request.ts (PROD-3312 appointment Billing
// tab). Only the device-booking discriminator the appointment page's Billing gate needs is ported; pm-react's device
// EDIT body builders (PROD-2526) are not part of the PA.
import { EMPTY_GUID } from '@/shared/lib/empty-guid';
import type { AppointmentResponse } from '@/types/scheduling-api';

interface ParticipantLike {
  referenceType?: string | null;
  reference?: string | null;
  display?: string | null;
  provider?: unknown;
}

/** The `Device` participant, which is what makes this a device booking. */
export function findDeviceParticipant<T extends ParticipantLike>(
  participants: readonly T[] | undefined | null,
): T | undefined {
  return participants?.find(
    (p) => (p.referenceType ?? '').toLowerCase() === 'device',
  );
}

function hasProviderParticipant(appt: AppointmentResponse): boolean {
  return Boolean(
    (appt.participant as ParticipantLike[] | undefined)?.some((p) => {
      const rt = (p.referenceType ?? '').toLowerCase();
      return rt === 'provider' || rt === 'practitioner';
    }),
  );
}

/**
 * Is this appointment a device booking?
 *
 * Both halves are required, and in this order. "Has a Device participant" alone
 * would misroute a hypothetical appointment that has BOTH (a practitioner using a
 * machine) into the device rail, which offers no provider control — that would be
 * a regression of the revenue-carrying path. A device booking is defined by the
 * ABSENCE of a provider, which is exactly how the server reads it
 * (`providerParticipant?.Reference` → null → the device branches).
 */
export function isDeviceAppointment(
  appt: AppointmentResponse | null | undefined,
): boolean {
  if (!appt) return false;
  return !hasProviderParticipant(appt) && !!findDeviceParticipant(appt.participant as ParticipantLike[]);
}

/** The device this booking is on. */
export function deviceIdOf(appt: AppointmentResponse): string | null {
  const ref = findDeviceParticipant(appt.participant as ParticipantLike[])?.reference;
  return ref && ref !== EMPTY_GUID ? ref : null;
}

/** The device's display name when the wire carried one. */
export function deviceNameOf(appt: AppointmentResponse): string | null {
  const p = findDeviceParticipant(appt.participant as ParticipantLike[]);
  return p?.display?.trim() || null;
}
